import { z } from 'zod';
import type { ContactDto, CreateContactCommand, UpdateContactCommand } from '@/api/generated';
import { useCreateContact, useUpdateContact } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';

const ROLE_SUGGESTIONS = [
  'Bouwheer',
  'Hoofdaannemer',
  'Architect',
  'Veiligheidscoördinator',
  'EPB-verslaggever',
  'Stabiliteitsingenieur',
  'Onderaannemer',
  'Leverancier',
  'Notaris',
  'Verzekeraar',
];

const schema = z.object({
  name: z.string().trim().min(2, 'Minstens 2 tekens'),
  role: z.string().trim().min(2, 'Minstens 2 tekens'),
  company: z.string().trim().max(120, 'Maximaal 120 tekens').optional().or(z.literal('')),
  email: z.string().trim().email('Geen geldig e-mailadres').optional().or(z.literal('')),
  phone: z.string().trim().max(40, 'Maximaal 40 tekens').optional().or(z.literal('')),
});

type ContactFormValues = z.infer<typeof schema>;

function emptyToNull(v: string | undefined | null): string | null {
  if (v === undefined || v === null) return null;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function toCreateCommand(values: ContactFormValues): CreateContactCommand {
  return {
    name: values.name.trim(),
    role: values.role.trim(),
    company: emptyToNull(values.company),
    email: emptyToNull(values.email),
    phone: emptyToNull(values.phone),
  };
}

function toUpdateCommand(values: ContactFormValues): UpdateContactCommand {
  return {
    name: emptyToNull(values.name),
    role: emptyToNull(values.role),
    company: emptyToNull(values.company),
    email: emptyToNull(values.email),
    phone: emptyToNull(values.phone),
  };
}

function ContactFormBody({
  initialValues,
  onSubmit,
  submitLabel,
}: {
  initialValues: ContactFormValues;
  onSubmit: (values: ContactFormValues) => Promise<void> | void;
  submitLabel: string;
}) {
  return (
    <Form<ContactFormValues>
      schema={schema}
      defaultValues={initialValues}
      onSubmit={async (values) => {
        await onSubmit(values);
      }}
    >
      <FormField<ContactFormValues> name="name" label="Naam" required>
        {(field) => <Input {...field} placeholder="Volledige naam" autoFocus />}
      </FormField>
      <FormField<ContactFormValues> name="role" label="Rol" required>
        {(field) => (
          <>
            <Input {...field} list="contact-role-suggestions" placeholder="Bv. Architect" />
            <datalist id="contact-role-suggestions">
              {ROLE_SUGGESTIONS.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
          </>
        )}
      </FormField>
      <FormField<ContactFormValues> name="company" label="Bedrijf">
        {(field) => <Input {...field} value={field.value ?? ''} placeholder="Bedrijfsnaam" />}
      </FormField>
      <FormField<ContactFormValues> name="email" label="E-mail">
        {(field) => (
          <Input {...field} value={field.value ?? ''} type="email" placeholder="naam@bedrijf.be" />
        )}
      </FormField>
      <FormField<ContactFormValues> name="phone" label="Telefoon">
        {(field) => <Input {...field} value={field.value ?? ''} type="tel" placeholder="+32 …" />}
      </FormField>
      <DialogFooter>
        <DialogClose asChild>
          <Btn type="button">Annuleer</Btn>
        </DialogClose>
        <FormSubmitButton>{submitLabel}</FormSubmitButton>
      </DialogFooter>
    </Form>
  );
}

export function ContactCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createMutation = useCreateContact();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuw contact</DialogTitle>
        <DialogDescription>Voeg iemand toe aan het adresboek van het project.</DialogDescription>
        <DialogBody>
          <ContactFormBody
            submitLabel="Toevoegen"
            initialValues={{ name: '', role: '', company: '', email: '', phone: '' }}
            onSubmit={async (values) => {
              await createMutation.mutateAsync(toCreateCommand(values));
              onOpenChange(false);
            }}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function ContactEditDialog({
  open,
  onOpenChange,
  contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactDto;
}) {
  const updateMutation = useUpdateContact(contact.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Bewerk contact</DialogTitle>
        <DialogDescription>Pas de gegevens van {contact.name} aan.</DialogDescription>
        <DialogBody>
          <ContactFormBody
            submitLabel="Opslaan"
            initialValues={{
              name: contact.name,
              role: contact.role,
              company: contact.company ?? '',
              email: contact.email ?? '',
              phone: contact.phone ?? '',
            }}
            onSubmit={async (values) => {
              await updateMutation.mutateAsync(toUpdateCommand(values));
              onOpenChange(false);
            }}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
